import { forwardRef, ReactNode } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import styles from "./ListTitleStoreResultCard.module.scss";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import MiscellaneousTag from "../../../../common/icons/MiscellaneousTag.tsx";
import ListTitleResultCard from "./ListTitleResultCard.tsx";
import { StoreAsset } from "../../../../common/api/decimal/common-types/manageAssets.ts";
import useStoreLinks from "../../../../common/features/store/useStoreLinks.ts";
import { getSearchQuery } from "../query.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { clsx } from "clsx";
import { useAssetDescriptors } from "../../../../common/features/asset/useAssetDescriptors.ts";
import AssetTypeTagLayout from "../../../../common/features/asset/AssetTypeTagLayout.tsx";

function ListTitleStoreResultCard(
  {
    asset,
    lazyLoad,
  }: {
    asset: StoreAsset;
    lazyLoad?: boolean;
  },
  ref: React.ForwardedRef<HTMLElement>
) {
  const intl = useIntl();
  const { makeStoreSearchUrl } = useStoreLinks();
  const [params] = useSearchParams();
  const query = getSearchQuery(params)!;
  const brand = useBrandLoaderData();
  const defaultTags = useAssetDescriptors(asset.manageAsset);
  const actualTags = asset.store.hasMultipleFormats
    ? [intl.formatMessage({ defaultMessage: "Multiple Formats" })]
    : defaultTags;
  const showPdpLink = !!brand.VirtualStore.companyId;
  const pdpLink = `/search/products/${asset.manageAsset.vbid}`;

  // note favorite popover removed as we can't distinguish store favorite
  // in favorites and the favorite is useless without an action,
  // want to add it back later when we work that out
  return (
    <ListTitleResultCard
      ref={ref}
      asset={asset}
      lazyLoad={lazyLoad}
      topRow={<AssetTypeTagLayout tags={actualTags} className={styles.tags} />}
      coverAction={showPdpLink ? { to: pdpLink } : undefined}
      buttons={
        <>
          {asset.store.priceStartingAt && (
            <div className={styles.price}>
              <div className={styles.priceLabel}>
                <FormattedMessage
                  defaultMessage="Available at:<div>{price}</div>"
                  values={{
                    price: asset.store.priceStartingAt,
                    div: (chunks: ReactNode) => (
                      <div className={styles.priceValue}>{chunks}</div>
                    ),
                  }}
                />
              </div>
            </div>
          )}
          {showPdpLink ? (
            <BaseLinkOrButton
              className={clsx("vst-button-pill", "vst-button-variant-outline")}
              to={pdpLink}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: "View Details for {title}",
                },
                { title: asset.manageAsset.title }
              )}
            >
              <FormattedMessage defaultMessage="View Details" />
            </BaseLinkOrButton>
          ) : (
            <BaseLinkOrButton
              className={"vst-button-pill"}
              href={makeStoreSearchUrl(asset.manageAsset.vbid, {
                utm_medium: "bookshelf-home-search",
                utm_campaign: query,
              })}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: "Purchase Options for {title}",
                },
                { title: asset.manageAsset.title }
              )}
            >
              <MiscellaneousTag />
              <FormattedMessage defaultMessage="Purchase Options" />
            </BaseLinkOrButton>
          )}
        </>
      }
      detailTags={
        asset.store.coachMe && (
          <div
            className={clsx("vst-feature-tag", "vst-feature-tag-type-coachme")}
          >
            <FormattedMessage defaultMessage="CoachMe Included" />
          </div>
        )
      }
    />
  );
}

const Card = forwardRef(ListTitleStoreResultCard);

export default Card;
